// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, type ReactNode } from 'react';

import type { AutodeskCloudModel } from '@evolvemep/api-ts';
import { Card, Flex, Switch } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { BasePageHeader, type BasePageHeaderProps } from 'components/Mantine/BasePageHeader';
import { GlobalProjectSelect } from 'components/Mantine/GlobalFilters/GlobalProjectSelect';
import { useProjectRequired } from 'components/Mantine/GlobalFilters/ProjectRequired';
import { ModelSelect } from 'modules/Shop/ModelBrowser/ModelSelect';
import { useModelSelection } from 'modules/Shop/ModelBrowser/useModelSelection';

type Props = {
  children: (urn: string | null, model: AutodeskCloudModel | null) => ReactNode;
} & BasePageHeaderProps;

export const ModelBrowserProvider = ({ children, ...basePageHeaderProps }: Props) => {
  const {
    selectedProject: { projectId },
  } = useProjectRequired();
  const [hideInaccessible, setHideInaccessible] = useState(true);
  const { selectedModel, modelSelectProps } = useModelSelection(projectId, { hideInaccessible });

  return (
    <>
      <BasePageHeader
        hideGlobalFilters
        {...basePageHeaderProps}
        topRightComponent={
          <>
            <Switch
              label={
                <>
                  Hide inaccessible content{' '}
                  <AgGridStyleTooltip
                    label="Hides models in Autodesk projects that you do not have access to."
                    openDelay={0}
                    withArrow
                  >
                    <EvolveIcon color="gray.7" inline icon="Info" size="sm" />
                  </AgGridStyleTooltip>
                </>
              }
              onChange={(e) => setHideInaccessible(e.currentTarget.checked)}
              checked={hideInaccessible}
            />
            <GlobalProjectSelect />
            <ModelSelect {...modelSelectProps} />
            {basePageHeaderProps.topRightComponent}
          </>
        }
      />
      <Flex gap="md" w="100%" h="100%" style={{ overflow: 'hidden' }}>
        <Card p={0} withBorder flex={1} display="flex" style={{ minWidth: 600, flexDirection: 'row' }}>
          {children(selectedModel?.urn ?? null, selectedModel?.evolveModel ?? null)}
        </Card>
      </Flex>
    </>
  );
};
